import { createApi } from '@reduxjs/toolkit/query/react'
import { baseApi } from './../utils'
import type {
	ApplicableReq,
	ApplicableTarrifBands,
	ChargeableItem,
	ResBusinessVehicleDefaultsArgs,
	VehicleAllotment,
	SupplierBranchesData,
	LiveReservationsData,
} from '../types'
import type { BespokeSelectOption } from './../../../components/Reservations/orderDetails/BespokeFields/BespokeFieldSelect'
import type { BespokeField } from './../../../components/Reservations/orderDetails/types'
import { ApplicableTariffBandOptionsType, ResBusinessDefaultsType } from '@view/contexts/FodContainerContext/types'
import { FnolFormPostData } from '@view/components/AccidentManagement/types'
import { UpdateLinkedTariffsResponse, LinkedTariffsType } from '@view/components/Tariffs/LinkedTariffs/types'
import { CountriesList } from '@view/pages/Business/BusinessContent/MainAddressModal/types'

type SidebarNavLink = {
	className: null | string
	icon: string | null
	link: string | null
	subMenu: null | string
	title: string
}
export type SemanticBespokeSelectOption = BespokeSelectOption & { name?: BespokeSelectOption['text']; value?: BespokeSelectOption['id'] }
export interface ChargeableAdaptor {
	ids: string[]
	items: { id: string; value: string; text: string }[]
	rates: {
		[key: string]: ChargeableItem['rates']
	}
}

export const ajaxApi = createApi({
	reducerPath: 'ajax',
	...baseApi('/ajax/'),
	tagTypes: ['Vehicles', 'Approvals', 'Notes', 'Tariffs', 'RentalReservationNotes'],
	endpoints(builder) {
		return {
			getApplicableTarrifBands: builder.query<ApplicableTariffBandOptionsType[], ApplicableReq>({
				query(args) {
					return `/get-applicable-tariffBands/?rentalVehicleGroupId=${args.vehicleGroupId}&numberOfDays=${
						args.totalDays
					}&businessId=${args.businessId}&intlTariffBandId=null&driverId=${
						args.driverId ? args.driverId : null
					}&presetTariffBandId=null&rentalReservationId=${args.rentalReservationId ? args.rentalReservationId : null}&startDate=${
						args.startDate
					}&cuiPanelId=${args.cuiPanelId}&reasonForHireId=${args.reasonForHire ? args.reasonForHire : null}&collectionBranchId=${
						args.collectionBranchId
					}&deliveryBranchId=${args.deliveryBranchId}`
				},
				keepUnusedDataFor: 1,
				transformResponse(items: ApplicableTarrifBands[]): ApplicableTariffBandOptionsType[] {
					return items.length
						? items.map((item, index) => {
								const newItem: ApplicableTariffBandOptionsType = {
									text: item.name,
									value: item.id,
									minDays: item.minDays,
									key: item.id,
									stopgap: item.stopgap,
									index: index,
								}
								return newItem
						  })
						: []
				},
			}),
			getChargeableItems: builder.query<ChargeableAdaptor, { tariffBandId: string; numDays: number }>({
				query({ tariffBandId, numDays = 1 }) {
					return `/get-chargeable-items-for-tariff/?tariffBandId=${tariffBandId}&numDays=${numDays}`
				},
				transformResponse(items: ChargeableItem[] = []): ChargeableAdaptor {
					function combineNetRatesForBundleItem(items: ChargeableItem[]) {
						const bundleItems = items.filter(({ id }) => id.includes(','))

						if (bundleItems.length == 0) {
							return items
						}

						const idRatesLookup = items.reduce((accum: { [key: string]: string }, item) => {
							if (item.rates) {
								accum[item.id] = item.rates.net
							}
							return accum
						}, {})

						const itemsWithNetCostAddedToBundleItems = items.map(item => {
							if (bundleItems.some(el => el.id == item.id)) {
								const bundleItemIds = item.id.split(',')
								const total = bundleItemIds.reduce((sum, id) => sum + Number(idRatesLookup[id]), 0)

								item.rates = { quantity: '1', net: total.toString(), rate: total.toString() }
							}

							return item
						})

						return itemsWithNetCostAddedToBundleItems
					}

					const formattedItems = combineNetRatesForBundleItem(items)

					/* grab the initial resopnse */
					const initial: ChargeableAdaptor = { ids: [], rates: {}, items: [] }
					/* normalise it by hand */

					if (formattedItems.length > 0) {
						return formattedItems.reduce((accum, value: ChargeableItem) => {
							const id = value.id
							const ids = [...accum.ids, id]
							const rate = { ...value.rates, id, num: 1 }
							const rates = { ...accum.rates }
							rates[id] = rate
							const items = [...accum.items, { id: value.id, value: value.id, text: value.text }]
							accum = { ...accum, ids, rates, items }
							return accum
						}, initial)
					}
				},
			}),
			getBespokeFields: builder.query<BespokeField[], BespokeArgs>({
				query({ id, primaryDriverId, reasonForHireId, tariffBandId, startDate }) {
					const params = new URLSearchParams({
						id,
						primaryDriverId,
						reasonForHireId,
						tariffBandId,
					})
					if (startDate) {
						params.set('startDate', startDate)
					}

					return `/get-business-bespoke-fields/?${params.toString()}`
				},
				transformResponse(bespokeFields: BespokeField[]) {
					function bespokeFieldsOrder(bespokeFieldsList: BespokeField[]) {
						const sortedFields = bespokeFieldsList
							.filter(field => Number(field.sortOrder))
							.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))

						const fieldsInOrder = sortedFields.concat(bespokeFields.filter(field => !Number(field.sortOrder)))

						return fieldsInOrder
					}

					return bespokeFieldsOrder(bespokeFields)
				},
			}),

			getNavLinks: builder.query<SidebarNavLink[], void>({
				query() {
					return `/get-nav-links/`
				},
			}),
			saveFnolForm: builder.mutation<{ status: boolean; id: string | number }, { params: FnolFormPostData }>({
				query: post => ({
					url: '/accident-management/edit-accident-management.php',
					method: 'POST',
					body: post,
				}),
			}),
			getVehicleAllotments: builder.query<VehicleAllotment[], string>({
				query(resId) {
					return `get-res-vehicle-allotments/?reservationId=${resId}`
				},
			}),
			getResBusinessVehicleDefaults: builder.query<ResBusinessDefaultsType, ResBusinessVehicleDefaultsArgs>({
				query({ id, driverId, tariffBandIds }) {
					return `get-res-business-vehicle-defaults/?id=${id}&driverId=${driverId}&tariffBandIds=${tariffBandIds || ''}`
				},
				transformResponse(response: ResBusinessDefaultsType) {
					const { businessVehicleCodesReact, businessVehicleDefault, bookerConfirmationShowSalesQuote } = response

					return {
						businessVehicleCodesReact,
						bookerConfirmationShowSalesQuote: Number(bookerConfirmationShowSalesQuote),
						businessVehicleDefault,
					}
				},
			}),
			getTaxiQuotes: builder.mutation({
				query(body) {
					const settings = {
						url: 'get-taxi-quote/',
						method: 'POST',
						body,
					}
					return ajaxOptions(settings)
				},
				transformResponse(response) {
					const combinedTaxiProviders = Object.values(response.taxiProviders).flatMap(value => value)

					const formattedResponse = formatTaxiVehicles(combinedTaxiProviders)

					return {
						businessVehicleCodesReact: { car: formattedResponse },
						message: response.message,
						bookingFee: response.bookingFee,
						taxisAvailable: combinedTaxiProviders.length > 0,
					}
				},
			}),
			bookTaxi: builder.mutation({
				query(body) {
					const settings = { url: 'book-taxi/', method: 'POST', body }
					return ajaxOptions(settings)
				},
			}),
			getResBusinessLocationDefaults: builder.query<string, string>({
				query(id) {
					return `get-res-business-location-defaults/?id=${id}/`
				},
				transformResponse(response) {
					let reasonsForHire = response.reasonsForHire

					if (Array.isArray(reasonsForHire) && reasonsForHire.length) {
						reasonsForHire = reasonsForHire.reduce((accum, val) => {
							const newObj = {
								text: '',
								value: '',
								key: '',
								description: '',
							}
							newObj.text = val.reason
							newObj.value = val.id
							newObj.key = val.id
							if (val.archivedDateTime) {
								newObj.description = val.archivedDateTime
							}
							accum = [...accum, newObj]
							return accum
						}, [])
					} else {
						reasonsForHire = Object.entries(reasonsForHire).reduce((accum, [key, values]) => {
							accum = [...accum, { text: key, value: values.id }]
							return accum
						}, [])
					}

					return { ...response, reasonsForHire }
				},
			}),
			getResBusinessOrderDetailsDefaults: builder.query<string, { id: string; rentalVehicleId: string }>({
				query({ id, rentalVehicleId, isTaxi }) {
					return `get-res-business-order-defaults/?id=${id}&rentalVehicleId=${rentalVehicleId}&isTaxi=${isTaxi}`
				},
				transformResponse(response) {
					const validCostCentre = response?.validCostCentre?.map(item => ({
						key: item,
						text: item,
						value: item,
					}))

					return { ...response, validCostCentre }
				},
			}),
			getLinkedTariffs: builder.query<{ id: string }, LinkedTariffsType>({
				query({ id }) {
					return `get-child-tariffs.php?parentTariffId=${id}`
				},
				providesTags: ['Tariffs'],
			}),
			updateLinkedTariffs: builder.mutation<
				UpdateLinkedTariffsResponse,
				{ tariffs: string[]; tariffName: string; cuiPanel: string; startDate: string; endDate: string; reason: string }
			>({
				query({ tariffs, tariffName, cuiPanel, startDate, endDate, reason }) {
					return {
						url: 'update-child-tariff-bands.php',
						method: 'POST',
						body: {
							tariffBandIds: tariffs.join(','),
							tariffName: tariffName,
							cuiPanel: cuiPanel,
							startDate: startDate,
							endDate: endDate,
							reason: reason,
						},
					}
				},
				invalidatesTags: ['Tariffs'],
			}),
			getIntuitiveAlerts: builder.query<{}, null>({
				query({ driverId }) {
					return `get-intuitive-alerts/?id=${driverId}`
				},
			}),
			getCountryList: builder.query<{ key: number; text: string; value: number; countryCode: string; countryPrefix: string }[], void>(
				{
					query() {
						return 'get-country-list'
					},
					transformResponse(countries: CountriesList) {
						return countries.map(({ id, name, countryCode: countrycode, countryPrefix: countryprefix }) => ({
							key: id,
							text: name,
							value: id,
							countrycode,
							countryprefix,
						}))
					},
				}
			),
			getPoolFleetVehicles: builder.query({
				query({ addressId, businessId, startDate, endDate }) {
					return `get-vehicles-by-lat-long/?addressId=${addressId}&businessId=${businessId}&startDate=${startDate}&endDate=${endDate}&availability=unavailable`
				},
				transformResponse(vehicles) {
					if (vehicles.length > 1) {
						return vehicles.toSorted((a, b) => {
							return a.distance - b.distance
						})
					}

					return vehicles
				},
			}),
			getDriverBespokeFieldsByBusiness: builder.query({
				query({ businessId }) {
					return `get-driver-bespoke-fields.php?businessId=${businessId}`
				},
			}),
			getAllSupplierBranches: builder.query<SupplierBranchesData, void>({
				query() {
					return `get-all-supplier-branches`
				},
				transformResponse(data: SupplierBranchesData) {
					return data.map(branch => ({
						...branch,
						id: branch.branchId,
						lattitude: +branch.lat,
						longitude: +branch.long,
						addressId: branch.branchId,
						position: {
							lat: +branch.lat,
							lng: +branch.long,
						},
						background: 'red',
					}))
				},
			}),
			getLiveReservationsForTariffBand: builder.query<LiveReservationsData, { tariffBandIds: string[] }>({
				query({ tariffBandIds }) {
					return `get-live-reservations-for-tariff-band.php?tariffBandIds=${tariffBandIds.join()}`
				},
				keepUnusedDataFor: 1,
			}),
			getTariffBandsForRes: builder.query({
				query({ resId, businessId }) {
					return `get-applicable-tariffBands.php?rentalReservationId=${resId}&businessId=${businessId}`
				},
				transformResponse(data) {
					if (data && Array.isArray(data)) {
						return data.map(({ id, name }) => ({ key: id, text: name, value: id }))
					}
				},
			}),
			updateTariffs: builder.mutation({
				query: post => ({
					url: '/update-tariff.php',
					method: 'POST',
					body: post,
				}),
			}),
		}
	},
})

export function formatTaxiVehicles(vehicleList) {
	return vehicleList.map(taxi => ({
		...taxi,
		code: taxi.provider.toUpperCase(),
		example: taxi.taxiExample,
		pictureUrl: taxi.taxiImageUrl ? taxi.taxiImageUrl : '/images/themes/fod/fod-taxi.svg',
		taxiVehicle: true,
		min: taxi.lowPrice,
		max: taxi.highPrice,
	}))
}

export function ajaxOptions({
	url,
	method,
	body,
	headers = {
		'Content-Type': 'application/json',
		'x-react': 'useReact',
	},
	...otherProps
} = {}) {
	return {
		url,
		method,
		body,
		headers,
		...otherProps,
	}
}

interface BespokeArgs {
	id: string
	primaryDriverId: string
	reasonForHireId: string
	tariffBandId: string
	startDate: string
}

export const {
	useGetApplicableTarrifBandsQuery,
	useGetNavLinksQuery,
	useGetChargeableItemsQuery,
	useGetBespokeFieldsQuery,
	useSaveFnolFormMutation,
	useGetVehicleAllotmentsQuery,
	useGetResBusinessVehicleDefaultsQuery,
	useGetResBusinessLocationDefaultsQuery,
	useGetResBusinessOrderDetailsDefaultsQuery,
	useGetLinkedTariffsQuery,
	useGetTaxiQuotesMutation,
	useBookTaxiMutation,
	useGetIntuitiveAlertsQuery,
	useGetCountryListQuery,
	useGetPoolFleetVehiclesQuery,
	useExpireLinkedTariffsMutation,
	useGetDriverBespokeFieldsByBusinessQuery,
	useUpdateLinkedTariffsMutation,
	useGetAllSupplierBranchesQuery,
	useGetLiveReservationsForTariffBandQuery,
	useGetTariffBandsForResQuery,
	useUpdateTariffsMutation,
} = ajaxApi
